// What the scene graph is actually doing, as against what the resolver intended.
// See docs/PERSISTENT_CANVAS_ARCHITECTURE.md.

import type { Object3D } from "three";
import { SCENE_LABEL, STAGE_TAG, type ActiveStage } from "@/lib/hooks/3d/use-stage-transition";

export type StageVisibility = {
  mounted: Exclude<ActiveStage, null>[];
  visible: Exclude<ActiveStage, null>[];
  /** Mounted, drawing nothing, and still answering a raycast — the SW-1792 signature. */
  hiddenButHittable: Exclude<ActiveStage, null>[];
};

export const EMPTY_STAGE_VISIBILITY: StageVisibility = {
  mounted: [],
  visible: [],
  hiddenButHittable: [],
};

export type SceneElement = {
  stage: Exclude<ActiveStage, null>;
  /** What this group is, as the component that mounted it named it. */
  label: string;
  /** Mesh, Points, Line, LineSegments — what three.js is actually drawing. */
  kind: string;
  points: number;
  visible: boolean;
  /** Still answers a raycast. Invisible and hoverable is the combination to look for. */
  hoverable: boolean;
};

function labelOf(object: Object3D): string {
  let node: Object3D | null = object;
  while (node) {
    const label = node.userData?.[SCENE_LABEL] as string | undefined;
    if (label) return label;
    node = node.parent;
  }
  return "unlabelled";
}

function pointCount(object: Object3D): number {
  const geometry = (object as { geometry?: { attributes?: { position?: { count?: number } } } })
    .geometry;
  return geometry?.attributes?.position?.count ?? 0;
}

/**
 * Every drawable thing in the scene, by name. `stages()` says which stage is mounted; this says
 * what it actually is — the published path, the one still downloading, the slice, the mesh.
 */
export function readSceneElements(content: Object3D | null | undefined): SceneElement[] {
  if (!content) return [];

  const elements: SceneElement[] = [];
  content.traverse((object) => {
    const stage = stageOf(object);
    if (!stage) return;
    if (!("material" in object) || !(object as { material?: unknown }).material) return;

    // A material alone is not geometry. The hover tooltip anchors a geometry-less <mesh> to
    // position its HTML, and R3F gives that a default material, so it read as a scene element
    // with no points — chrome again, listed as data (SW-1792).
    const points = pointCount(object);
    if (points === 0) return;

    elements.push({
      stage,
      label: labelOf(object),
      kind: object.type,
      points,
      visible: object.visible,
      hoverable: object.userData?.baseRaycast === undefined,
    });
  });
  return elements;
}

function stageOf(object: Object3D): ActiveStage {
  let node: Object3D | null = object;
  while (node) {
    const tag = node.userData?.[STAGE_TAG] as ActiveStage | undefined;
    if (tag) return tag;
    node = node.parent;
  }
  return null;
}

export function readStageVisibility(content: Object3D | null | undefined): StageVisibility {
  if (!content) return EMPTY_STAGE_VISIBILITY;

  const mounted = new Set<Exclude<ActiveStage, null>>();
  const visible = new Set<Exclude<ActiveStage, null>>();
  const hittable = new Set<Exclude<ActiveStage, null>>();

  content.traverse((object) => {
    const stage = stageOf(object);
    if (!stage) return;
    const drawable = "material" in object && !!(object as { material?: unknown }).material;
    if (!drawable) return;

    mounted.add(stage);
    if (object.visible) visible.add(stage);
    else if (object.userData?.baseRaycast === undefined) hittable.add(stage);
  });

  const sorted = (set: Set<Exclude<ActiveStage, null>>) => [...set].sort();
  return {
    mounted: sorted(mounted),
    visible: sorted(visible),
    hiddenButHittable: sorted(hittable),
  };
}

// The scene lives inside the R3F reconciler and the telemetry emitter runs outside it, so the
// in-canvas SceneInspector publishes a reader rather than a snapshot — read at emit time, not
// recomputed every frame.
let readLive: (() => StageVisibility) | null = null;

export function publishStageVisibilityReader(read: (() => StageVisibility) | null) {
  readLive = read;
}

export function currentStageVisibility(): StageVisibility {
  try {
    return readLive?.() ?? EMPTY_STAGE_VISIBILITY;
  } catch {
    return EMPTY_STAGE_VISIBILITY;
  }
}

export type SceneElementGroup = {
  label: string;
  kind: string;
  count: number;
  points: number;
  visible: boolean;
  hoverable: boolean;
};

/**
 * The same label and kind repeated — three traces of one path, nine lines of one grid — read as
 * noise element by element. Collapse them, keeping the totals and flagging the mixed case.
 */
export function groupSceneElements(elements: SceneElement[]): SceneElementGroup[] {
  const groups = new Map<string, SceneElementGroup>();
  for (const element of elements) {
    const key = `${element.label}\u0000${element.kind}`;
    const existing = groups.get(key);
    if (!existing) {
      groups.set(key, {
        label: element.label,
        kind: element.kind,
        count: 1,
        points: element.points,
        visible: element.visible,
        hoverable: element.hoverable,
      });
      continue;
    }
    existing.count += 1;
    existing.points += element.points;
    // Any visible member makes the group visible; any hoverable member makes it hoverable, so a
    // hidden-but-hittable object is never hidden by its neighbours.
    existing.visible ||= element.visible;
    existing.hoverable ||= element.hoverable;
  }
  return [...groups.values()];
}

export type SceneElementRow = {
  label: string;
  visible: boolean;
  hoverable: boolean;
  /** One entry per primitive kind: a trace is drawn as points *and* as the line through them. */
  parts: { kind: string; count: number; points: number }[];
};

/**
 * One row per thing in the scene. A single trace renders as a Points object and a LineSegments
 * object over the same data, which listed separately reads as the same geometry twice.
 */
export function groupSceneRows(groups: SceneElementGroup[]): SceneElementRow[] {
  const rows = new Map<string, SceneElementRow>();
  for (const group of groups) {
    const row = rows.get(group.label);
    const part = { kind: group.kind, count: group.count, points: group.points };
    if (!row) {
      rows.set(group.label, {
        label: group.label,
        visible: group.visible,
        hoverable: group.hoverable,
        parts: [part],
      });
      continue;
    }
    row.parts.push(part);
    row.visible ||= group.visible;
    row.hoverable ||= group.hoverable;
  }
  return [...rows.values()];
}
